import {
  HONOR_PLAQUE_H,
  HONOR_PLAQUE_W,
  HONOR_TITLES,
  TYPE,
  honorFromWins,
  honorIndexFromWins,
  type HonorTitle,
} from "@gaoya/shared";
import type { LeaderRow } from "../../game/state.js";
import { C, clipName, drawPanel, fillRound, strokeRound } from "../theme.js";

export function drawHonorPlaque(
  ctx: CanvasRenderingContext2D,
  x: number,
  cy: number,
  title: HonorTitle,
): number {
  ctx.save();
  const h = HONOR_PLAQUE_H;
  const w = HONOR_PLAQUE_W;
  const y = cy - h / 2;

  fillRound(ctx, x, y, w, h, 4, title.wood);
  fillRound(ctx, x + 1, y + h - 4, w - 2, 3, 2, "rgba(90,58,28,0.16)");
  ctx.strokeStyle = "rgba(255,248,235,0.5)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 4, y + 1.5);
  ctx.lineTo(x + w - 4, y + 1.5);
  ctx.stroke();

  strokeRound(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 4, title.stroke, title.strokeWidth);
  if (title.gilt) {
    strokeRound(ctx, x + 2.2, y + 2.2, w - 4.4, h - 4.4, 2.6, title.stroke, 0.65);
  }

  ctx.fillStyle = title.text;
  ctx.font = `600 11px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(title.name, x + w / 2, cy + 0.4);
  ctx.beginPath();
  ctx.restore();
  return w;
}

export const HONOR_WINS_COL = 52;

export function drawWinsCol(
  ctx: CanvasRenderingContext2D,
  wins: number,
  right: number,
  cy: number,
  size: number,
): void {
  ctx.save();
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.seal;
  ctx.font = `700 ${size}px ${TYPE.sans}`;
  ctx.fillText(String(wins), right - 18, cy);
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText("胜", right, cy);
  ctx.restore();
}

/** 名字紧跟小木牌。`rightLimit` 为胜场列左缘，牌贴在名字右侧。 */
export function drawNameWithHonor(
  ctx: CanvasRenderingContext2D,
  rawName: string,
  wins: number,
  x: number,
  cy: number,
  rightLimit: number,
): void {
  const title = honorFromWins(wins);
  const plaqueW = HONOR_PLAQUE_W;
  const gap = 6;
  const nameMax = Math.max(20, rightLimit - x - plaqueW - gap);
  const label = clipName(ctx, rawName, nameMax);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x, cy);
  const nw = ctx.measureText(label).width;
  ctx.save();
  drawHonorPlaque(ctx, x + nw + gap, cy, title);
  ctx.restore();
}

/** 总榜下方头衔阶梯的固定高度：标题行 + 三行三列。 */
export const HONOR_LADDER_H = 186;

/** 九阶头衔 + 所需胜场。当前档位浅金底。 */
export function drawHonorLadder(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  currentWins = 0,
): void {
  drawPanel(ctx, x, y, w, h, 16, C.card);
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink;
  ctx.font = `700 14px ${TYPE.serif}`;
  ctx.textAlign = "left";
  ctx.fillText("头衔阶梯", x + 16, y + 18);
  ctx.fillStyle = C.ink3;
  ctx.font = `11px ${TYPE.sans}`;
  ctx.textAlign = "right";
  ctx.fillText("各级所需胜场", x + w - 16, y + 18);

  const current = honorIndexFromWins(currentWins);
  const cols = 3;
  const rows = Math.ceil(HONOR_TITLES.length / cols);
  const gridX = x + 8;
  const gridY = y + 34;
  const gridW = w - 16;
  const gridH = h - 42;
  const cellW = gridW / cols;
  const cellH = gridH / rows;
  for (let i = 0; i < HONOR_TITLES.length; i++) {
    const title = HONOR_TITLES[i];
    if (!title) continue;
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridX + col * cellW;
    const cy = gridY + row * cellH;
    if (i === current) fillRound(ctx, cx + 2, cy + 1, cellW - 6, cellH - 4, 8, "rgba(228,185,95,0.22)");
    const plaqueX = cx + (cellW - HONOR_PLAQUE_W) / 2;
    const plaqueCy = cy + Math.max(12, cellH * 0.38);
    ctx.save();
    drawHonorPlaque(ctx, plaqueX, plaqueCy, title);
    ctx.restore();
    ctx.textAlign = "center";
    ctx.fillStyle = C.seal;
    ctx.font = `600 11px ${TYPE.sans}`;
    ctx.fillText(`${title.minWins}胜`, cx + cellW / 2, plaqueCy + HONOR_PLAQUE_H / 2 + 12);
  }
}

const PREVIEW_NICKS = ["墨白", "青梧", "疏影", "问樵", "拾花", "听雨", "折柳", "扫叶", "坐忘"];

/** 浏览器打开 `?honor=1` 时用九阶样例核对木牌。 */
export function honorPreviewActive(): boolean {
  try {
    return typeof location !== "undefined" && /[?&#]honor=1(?:&|$)/.test(location.href);
  } catch {
    return false;
  }
}

export function honorPreviewRows(): LeaderRow[] {
  return HONOR_TITLES.map((t, i) => ({
    nickname: PREVIEW_NICKS[i] ?? t.name,
    wins: t.minWins === 0 ? 2 : t.minWins,
  }));
}
